import { friendlyError, type ApiError } from "../../api/client.js";

export interface CatalogAppSummary {
  slug: string;
  name: string;
  description: string;
  category: string | null;
  icon: string | null;
  resources: { mem_mb: number; cpus: number };
  variable_count: number;
}

export type AppVariableType = "string" | "password" | "number" | "enum";

export interface AppVariable {
  key: string;
  label: string;
  type: AppVariableType;
  required: boolean;
  default?: string;
  enum_values?: string[];
  regex?: string;
  /** Render-as-link hint for the value — a boolean flag, NOT a URL string. */
  url: boolean;
  help?: string;
  /** Server generates this secret at deploy/update — no input rendered. */
  generate?: boolean;
  length?: number;
}

export interface AppHealthcheck {
  type: "none" | "docker" | "http";
  warmup_seconds: number;
  timeout_seconds: number;
}

export interface CatalogAppDetail {
  slug: string;
  name: string;
  description: string;
  category: string | null;
  icon: string | null;
  /** Display-only template version (written to template_commit). */
  version: string;
  exposed_port: number | null;
  variables: AppVariable[];
  healthcheck: AppHealthcheck;
}


export interface AppInstance {
  id: number;
  template_slug: string;
  name: string;
  subdomain: string | null;
  url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  last_health_check_at: string | null;
  last_action_at: string | null;
}

export interface AppLiveEntry {
  service: string;
  state: string;
  health: string;
}

export interface AppInstanceDetail extends AppInstance {
  template_name: string | null;
  template_commit: string | null;
  variables: { key: string; value: string }[];
  live: AppLiveEntry[] | null;
}

/** Statuses where no background task owns the row — polling stops here.
 * Everything else (pending/creating/updating/deleting/degraded) polls. */
export const APP_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  "running",
  "stopped",
  "failed",
]);

// Status COLOURS live in vmUtils.statusColor (additive app-status cases) —
// StatusPill sources them there, so there is deliberately no local colour map.

export function appStatusCopy(status: string): string {
  switch (status) {
    case "pending":
      return "Queued";
    case "creating":
      return "Setting up";
    case "running":
      return "Running";
    case "degraded":
      return "Needs attention";
    case "stopped":
      return "Stopped";
    case "failed":
      return "Failed";
    case "deleting":
      return "Deleting";
    case "updating":
      return "Updating — pulling new images and recreating containers";
    default:
      return status;
  }
}

/**
 * Error-code → customer-facing copy for the /api/apps surface, mirroring
 * backupActionError's `{title?, message}` shape. Falls through to
 * friendlyError for anything unmapped.
 */
export function appActionError(
  err: ApiError,
  fallback: string
): { title?: string; message: string } {
  switch (err.error) {
    case "app_slot_taken":
      return {
        message:
          "You already have an instance of this app — delete it first if you want a fresh one.",
      };
    case "app_subdomain_taken":
      return {
        title: "Address already in use",
        message:
          "The web address for this app is taken. Try again in a moment — if it keeps happening, contact support.",
      };
    case "app_deploy_in_progress":
      return {
        message:
          "Another app is already being set up for your account. Wait for it to finish, then try again.",
      };
    case "no_app_hosts":
      return {
        title: "Apps unavailable",
        message:
          "No app servers are available right now. Please try again later or contact support.",
      };
    case "insufficient_host_capacity":
      return {
        title: "Not enough capacity",
        message:
          "The app server doesn't have enough free memory for this app right now. Please try again later.",
      };
    case "app_invalid_state":
      return {
        message:
          "That action isn't available while the app is in its current state — refresh and try again.",
      };
    case "app_update_in_progress":
      return {
        title: "Update already running",
        message:
          "This app is already being updated. Wait for the update to finish, then try again.",
      };
    case "app_template_changed":
      return {
        title: "App can't be updated automatically",
        message:
          "This app's settings no longer match its template, so it can't be updated automatically. Please contact support.",
      };
    case "unknown_app_host":
      return {
        title: "App server not found",
        message: "The selected app server no longer exists — refresh and try again.",
      };
    case "app_host_unavailable":
      return {
        title: "App server unreachable",
        message: "The app server couldn't complete that right now. Try again in a moment.",
      };
    case "app_command_failed":
      return {
        message:
          "The app didn't respond to that command. Please try again in a moment.",
      };
    case "invalid_subdomain":
      return {
        title: "Couldn't create the app",
        message:
          "We couldn't build a valid web address for this app — please contact support.",
      };
    case "reserved_subdomain":
      return {
        title: "Couldn't create the app",
        message:
          "This app's address is reserved by the operator — please contact support.",
      };
    case "unknown_app":
      return {
        title: "App not found",
        message: "This app doesn't exist or is no longer offered.",
      };
    case "invalid_request":
      return {
        title: "Something's wrong with the request",
        message: "Please refresh the page and try again.",
      };
    case "not_found":
      return {
        title: "Not found",
        message: "That app no longer exists.",
      };
    default:
      return { message: friendlyError(err, fallback) };
  }
}
